// Brand palettes don't guarantee the accents are legible against base or
// contrast (pastel accents are common, and a dark-mode host flips contrast to
// light while the accents stay put). dialog.css never pairs an accent with
// base/contrast directly; it goes through the derived vars set here. Also
// imported by dashboard/src/AppearancePreview.svelte.

type Rgb = [number, number, number];

const AA = 4.5;

// Overrides for `el`'s inline style; "" means fall back to the dialog.css
// default. Re-run whenever the palette may have changed. `el` must be
// connected and inside the scope where the --_ aliases resolve. Any color the
// browser can compute works (hwb, oklch, color-mix…): the canvas converts it
// to sRGB.
export function accentContrastVars(el: Element): Record<string, string> {
  const vars = { "--_on-accent": "", "--_on-accent-2": "", "--_accent-ink": "" };
  const accent = resolve(el, "--_accent-1");
  const accent2 = resolve(el, "--_accent-2");
  const base = resolve(el, "--_base");
  const base2 = resolve(el, "--_base-2");
  const contrast = resolve(el, "--_contrast");
  if (!accent || !accent2 || !base || !base2 || !contrast) return vars;

  // Each default is kept while it passes, so most palettes render unchanged.
  if (worse(accent, base, contrast)) vars["--_on-accent"] = "var(--_contrast)";
  if (worse(accent2, contrast, base)) vars["--_on-accent-2"] = "var(--_base)";

  // Links sit on --_base-2 (assistant bubble) as well as --_base.
  const inkOk = (c: Rgb) => Math.min(ratio(c, base), ratio(c, base2)) >= AA;
  if (!inkOk(accent)) {
    for (let p = 10; p <= 100; p += 10) {
      if (inkOk(mix(accent, contrast, p / 100))) {
        vars["--_accent-ink"] = `color-mix(in srgb, var(--_accent-1), var(--_contrast) ${p}%)`;
        break;
      }
    }
  }

  return vars;
}

let ctx: CanvasRenderingContext2D | null | undefined;

function resolve(el: Element, cssVar: string): Rgb | null {
  const probe = document.createElement("span");
  probe.style.cssText = `display:none;color:var(${cssVar})`;
  el.appendChild(probe);
  const color = getComputedStyle(probe).color;
  probe.remove();

  ctx ??= document.createElement("canvas").getContext("2d", { willReadFrequently: true });
  if (!ctx) return null;
  ctx.clearRect(0, 0, 1, 1);
  ctx.fillStyle = "#000";
  ctx.fillStyle = color;
  ctx.fillRect(0, 0, 1, 1);
  const d = ctx.getImageData(0, 0, 1, 1).data;
  return [d[0] ?? 0, d[1] ?? 0, d[2] ?? 0];
}

// Is `text` illegible on `bg` while `alt` would read better?
function worse(bg: Rgb, text: Rgb, alt: Rgb): boolean {
  const current = ratio(bg, text);
  return current < AA && ratio(bg, alt) > current;
}

function mix(a: Rgb, b: Rgb, t: number): Rgb {
  return a.map((v, i) => v + (b[i]! - v) * t) as Rgb;
}

function luminance(c: Rgb): number {
  const [r, g, b] = c.map((v) => {
    const s = v / 255;
    return s <= 0.04045 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  }) as Rgb;
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

function ratio(a: Rgb, b: Rgb): number {
  const la = luminance(a);
  const lb = luminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}
